import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action, set as emberSet } from '@ember/object';
import { service } from '@ember/service';
import { underscore } from '@ember/string';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { pick, queue } from '@nullvoxpopuli/ember-composable-helpers';
import t from 'ember-intl/helpers/t';
import PowerSelect from 'ember-power-select/components/power-select';
import set from 'ember-set-helper/helpers/set';
import { and, isArray, not, or } from 'ember-truth-helpers';
import LocalityAutocomplete from 'invite-sign/components/locality-autocomplete';
import DateField from 'invite-sign/components/mapped-field/date-field';
import EmailField from 'invite-sign/components/mapped-field/email-field';
import HostField from 'invite-sign/components/mapped-field/host-field';
import translator from 'invite-sign/helpers/translator';
import type Entry from 'invite-sign/models/entry';
import type SignInField from 'invite-sign/models/sign-in-field';
import type LocalizationService from 'invite-sign/services/localization';
import { DISCARDED_RESPONSE_STRINGS } from 'invite-sign/utils/enums';

const DEFAULT_LOCALE = 'en';

/**
 * Minimal interface describing the invite/entry attributes used by MappedField.
 * This allows both Invite and GroupInviteRegistration to be passed as the invite arg.
 */
export interface MappedFieldInviteArg {
  employeeScreeningFlow?: boolean;
  locality?: {
    'place-id'?: string;
  };
}

interface MappedFieldSignature {
  Args: {
    entry?: Entry;
    invite?: MappedFieldInviteArg;
    mappedField: SignInField;
  };
}

export default class MappedField extends Component<MappedFieldSignature> {
  @service declare localization: LocalizationService;

  get isInvalid() {
    return !this.isValid;
  }

  get isValid() {
    return isEmpty(this.args.mappedField?.errors);
  }

  get showFieldError(): boolean {
    if (!this.isInvalid) return false;
    if (this.args.mappedField?.isDate) return false;
    return true;
  }

  get error() {
    return underscore((<string[]>(this.args.mappedField.errors.messages as unknown))[0]?.toString() ?? '');
  }

  get dropdownSelectedValue() {
    const { value } = this.args.mappedField;

    if (value === DISCARDED_RESPONSE_STRINGS.PLACEHOLDER) {
      return '';
    }

    return value;
  }

  get inputFieldName() {
    return this.args.mappedField.identifier ?? this.args.mappedField.id;
  }

  get visitorRecord() {
    return this.args.invite ?? this.args.entry;
  }

  get localityType() {
    const localityType = this.args.mappedField.isFullAddress ? 'address' : undefined;

    if (localityType === 'address' && this.localization.languageCode !== DEFAULT_LOCALE) {
      // other locales may structure address data differently, so we default to
      // geocode which will actually return results
      return 'geocode';
    }

    return localityType;
  }

  @action
  validate() {
    this.args.mappedField.validate();
  }

  @action
  handleChange(selection: string) {
    const { signInFieldActions } = this.args.mappedField;

    if (signInFieldActions.length) {
      const rest = signInFieldActions.filter(
        ({ dropdownOption, isConditional }) => isConditional && dropdownOption.value !== selection,
      );

      rest.forEach((action) => {
        emberSet(action, 'actionableSignInField.value', '');
      });
    }
  }

  @action
  handleLocalityChange(placeId: string) {
    if (this.visitorRecord) {
      // New object marks the locality attribute as dirty
      this.visitorRecord.locality = { ...this.visitorRecord.locality, 'place-id': placeId };
    }
  }

  <template>
    <div
      data-test-mapped-wrapper
      data-test-field={{@mappedField.label}}
      class="antialiased {{if this.isInvalid 'error'}}"
    >
      <div class="mb-2" date-test-label>
        <label class="leading-tight grayDarker text-base" for={{@mappedField.id}}>
          {{! https://envoycom.atlassian.net/browse/VIS-2499 }}
          {{#if (and (not (isArray @mappedField.ipadLocalizedNames)) @mappedField.isName)}}
            {{t "your_full_name"}}
          {{else}}
            {{translator
              @mappedField.localizedNames
              languageCode=this.localization.languageCode
              defaultText=@mappedField.label
            }}
          {{/if}}
        </label>
        {{#if @mappedField.required}}
          <span data-test-required class="requiredField"></span>
        {{/if}}
      </div>
      {{#if @mappedField.isHost}}
        <HostField @mappedField={{@mappedField}} @id={{@mappedField.id}} />
      {{else if @mappedField.isEmail}}
        <EmailField @mappedField={{@mappedField}} @id={{@mappedField.id}} @entry={{@entry}} @invite={{@invite}} />
      {{else if @mappedField.isDate}}
        <DateField @mappedField={{@mappedField}} @id={{@mappedField.id}} />
      {{else if @mappedField.isDropdown}}
        <div data-test-dropdown-select>
          <PowerSelect
            @dropdownClass="dropdown-options"
            @options={{@mappedField.itemsValues}}
            @placeholder={{t "choose_option"}}
            @searchEnabled={{false}}
            @selected={{this.dropdownSelectedValue}}
            @allowClear={{not @mappedField.required}}
            @onChange={{queue (fn (mut @mappedField.value)) this.validate this.handleChange}}
            as |option|
          >
            <span data-private>{{option}}</span>
          </PowerSelect>
        </div>
      {{else if (or @mappedField.isFullAddress @mappedField.isLocality)}}
        <LocalityAutocomplete
          @id={{@mappedField.id}}
          @name={{@mappedField.value}}
          @updateName={{fn (mut @mappedField.value)}}
          @placeId={{this.visitorRecord.locality.place-id}}
          @updatePlaceId={{this.handleLocalityChange}}
          @type={{this.localityType}}
        />
      {{else}}
        <input
          data-test-input
          id={{@mappedField.id}}
          type="text"
          class="inputClass rounded-md"
          value={{@mappedField.value}}
          name={{this.inputFieldName}}
          disabled={{or (and @invite.employeeScreeningFlow @mappedField.isName) @mappedField.isDisabled}}
          autocomplete="off"
          {{! disable last pass autofill }}
          data-lpignore="true"
          {{! disable one pass autofill }}
          data-1p-ignore="true"
          {{! disable dashlane autofill }}
          data-form-type="other"
          {{on "input" (queue (pick "target.value" (set @mappedField "value")) this.validate)}}
        />
      {{/if}}
      {{#if this.showFieldError}}
        <div data-test-input-error class="bottom-error">{{t this.error}}</div>
      {{/if}}
    </div>
  </template>
}
